import { RichTextEditor, type RichTextEditorRef } from "mui-tiptap";
import { useCallback, useEffect, useRef } from "react";
import "./ARichTextEditor.css";
import "./ARichTextEditorMini.css";
import EditorMenuControls from "./EditorMenuControls";
import useExtensions, { type Mentionable } from "./useExtensions";

export interface Props {
  placeholder?: string;
  content?: string;
  isEditable?: boolean;
  mentions?: boolean;
  mentionables?: Mentionable[];
  className?: string;
  onChange?: (content: string) => void;
}

export default function ARichTextEditorOnlyHtml({
  placeholder,
  content,
  isEditable,
  mentions,
  mentionables,
  className,
  onChange,
}: Props) {
  const extensions = useExtensions({
    placeholder: placeholder ?? "",
    mentions: mentions,
    mentionables: mentionables,
  });
  const rteRef = useRef<RichTextEditorRef>(null);
  const editor = rteRef.current?.editor;

  const handleUpdate = useCallback(() => {
    const html = editor?.getHTML();
    onChange?.(html!);
  }, [onChange, editor]);

  useEffect(() => {
    if (!editor || editor.isDestroyed) {
      return;
    }
    if (!editor.isFocused || !editor.isEditable) {
      // Use queueMicrotask per https://github.com/ueberdosis/tiptap/issues/3764#issuecomment-1546854730
      //console.log('Set content: '+ content);
      if (content) {
        const lines = content.split("\n").map((x) => x.trimEnd());
        const htmlLines = lines.map((x) => (x.startsWith("<") ? x : `<p>${x}</p>`));
        const newContent = htmlLines.join("\n");
        queueMicrotask(() => {
          const currentSelection = editor.state.selection;
          editor
            .chain()
            .clearContent()
            .insertContent(newContent)
            .setTextSelection(currentSelection)
            .run();
        });
      } else {
        queueMicrotask(() => {
          editor.chain().clearContent().run();
        });
      }
    }
  }, [content, editor]);

  return (
    <RichTextEditor
      ref={rteRef}
      className={className ?? undefined}
      extensions={extensions}
      editable={isEditable}
      onUpdate={handleUpdate}
      renderControls={() => {
        return <EditorMenuControls />;
      }}
    />
  );
}
